<script lang="ts">
  import * as _ from "lodash";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import ProductChooserModal from "@distributor/svelte/SellSheet/Components/ProductChooserModal.svelte";
  import EditSellSheetProductModal from "@distributor/svelte/SellSheet/Components/EditSellSheetProductModal.svelte";
  import type { DistributorSellSheet } from "@shared/types/SellSheet";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import Flash from "@shared/services/flashv2.js";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import ProductContainerDetails from "@shared/filters/product_container_details.js";

  export let sellSheet: DistributorSellSheet;
  export let page: any;
  export let mobileMode: boolean;
  export let showAddProductsModal: boolean;

  $: showEditModal = false;
  let uneditableProducts = sellSheet?.distributor?.uneditable_sellsheet_products;
  let editingCopy;
  let errors;
  let selectMultipleChoices = {};
  let initialFilterStateParams: any = {};
  const manufacturer = _.get(window, "$rootScope.user.manufacturers[0]");
  if (manufacturer) {
    initialFilterStateParams["manufacturerId"] = manufacturer.id;
  }

  const arrayCleanup = function (input) {
    if (!input) {
      return "";
    }
    return input.toString().replace(/,/gi, ", ");
  };

  const prepSelected = function () {
    selectMultipleChoices = _.reduce(
      sellSheet.product_lines,
      function (obj, entry) {
        obj[entry.cloned_from_product_line_id] = true;
        return obj;
      },
      {},
    );
  };
  prepSelected();

  const remove = async function (productLine) {
    page.loadingProducts = true;
    delete selectMultipleChoices[productLine.cloned_from_product_line_id];

    try {
      await HttpPromise({
        method: "DELETE",
        url: `/api/distributor/sell_sheets/${sellSheet.id}/sell_sheet_product_lines/${productLine.id}`,
      });
      Flash.now({ message: `${productLine.name} removed from your sheet.`, type: "success" });
      _.remove(sellSheet.product_lines, productLine);
      sellSheet.product_lines = sellSheet.product_lines;
      page.loadingProducts = false;
    } catch (error) {
      errors = { messages: error };
      page.loadingProducts = false;
    }
  };

  const edit = function (productLine) {
    page.sortMode = false;
    editingCopy = _.cloneDeep(productLine);
    showEditModal = true;
  };

  const cancelEditsCB = function () {
    showEditModal = false;
    editingCopy = null;
  };

  const finishSaveEdits = function (res) {
    let ind = sellSheet.product_lines.findIndex(pl => pl.id === res.id);
    sellSheet.product_lines[ind] = res;
    sellSheet.product_lines = sellSheet.product_lines;

    showEditModal = false;
    editingCopy = null;
    page.loading = false;
    page.loadingProducts = false;
    Flash.now({ message: `Product updated!`, type: "success" });
  };

  const closeAddProductAndSave = async function (savedProducts) {
    page.loadingProducts = true;
    let clonedFromProductLineIds = _.map(Object.entries(savedProducts), function (productLine) {
      let sellSheetProductLine = sellSheet.product_lines.filter(function (product_line) {
        return product_line.cloned_from_product_line_id == parseInt(productLine[0]);
      });

      if (productLine[1]) {
        if (sellSheetProductLine.length == 0) {
          return { cloned_from_product_line_id: parseInt(productLine[0]) };
        }
      } else {
        if (sellSheetProductLine.length > 0) {
          return { id: sellSheetProductLine[0].id, _destroy: "1" };
        }
      }
    });

    try {
      let res = await HttpPromise({
        method: "PUT",
        url: `/api/distributor/sell_sheets/${sellSheet.id}`,
        data: {
          sell_sheet: {
            sell_sheet_product_lines_attributes: clonedFromProductLineIds,
          },
        },
      });
      sellSheet = res;
      showAddProductsModal = false;
      sellSheet.product_lines = sellSheet.product_lines;
      page.loadingProducts = false;
    } catch (error) {
      errors = { messages: error };
      showAddProductsModal = false;
      page.loadingProducts = false;
    }
  };

  const closeAddProduct = function () {
    showAddProductsModal = false;
  };

  const pullProductLines = async function (params, catalogPerPage, catalogPage) {
    params = _.merge(params, { distributor_id: sellSheet.distributor_id });
    if (initialFilterStateParams?.manufacturerId) {
      params.manufacturer_id = initialFilterStateParams.manufacturerId;
    }
    if (params.c) {
      params.category_id = params.c;
    }
    if (params.s) {
      params.subcategory_id = params.s;
    }
    if (params.t) {
      params.product_type_id = params.t;
    }
    if (params.n) {
      params.container_type = params.n;
    }
    if (params.m) {
      params.manufacturer_id = params.m;
    }
    if (params.b) {
      params.brand_family_id = params.b;
    }
    if (params.g) {
      params.tag_id = params.g;
    }
    if (params.v) {
      params.vintage_year = params.v;
    }
    if (params.r) {
      params.material = params.r;
    }
    let perPage = catalogPerPage ? catalogPerPage : 50;
    let res = await HttpPromise({
      method: "GET",
      url: "/api/distributor/product_lines/search",
      data: _.merge(
        {
          require_inventory: true,
          limit: perPage,
          offset: perPage * (catalogPage - 1),
        },
        params,
      ), // todo allow sorting in ES through distro app
    });
    return Promise.resolve({ data: res });
  };

  const pullAvailableFilters = async function (params) {
    params = _.merge(params, { specified_distributor_id: sellSheet.distributor_id });
    if (params.distributorSearch) {
      params.distributor_search = params.distributorSearch;
    } else {
      params.distributor_search = null;
    }
    if (params.productTypeSearch) {
      params.product_type_search = params.productTypeSearch;
    } else {
      params.product_type_search = null;
    }
    if (params.categorySearch) {
      params.category_search = params.categorySearch;
    } else {
      params.category_search = null;
    }
    if (params.subcategorySearch) {
      params.subcategory_search = params.subcategorySearch;
    } else {
      params.subcategory_search = null;
    }
    if (params.brandFamilySearch) {
      params.brand_family_search = params.brandFamilySearch;
    } else {
      params.brand_family_search = null;
    }
    if (params.manufacturerSearch) {
      params.manufacturer_search = params.manufacturerSearch;
    } else {
      params.manufacturer_search = null;
    }
    if (params.tagSearch) {
      params.tag_search = params.tagSearch;
    } else {
      params.tag_search = null;
    }
    if (manufacturer) {
      params.manufacturer_id = manufacturer.id;
    }

    let res = await HttpPromise({
      method: "GET",
      url: "/api/distributor/product_lines/available_filters",
      data: params,
    });
    return Promise.resolve({ data: res });
  };

  const getProductLinesInit = async function (params, catalogPerPage, catalogPage) {
    let res = await pullProductLines(params, catalogPerPage, catalogPage);
    page.searchResults = res.data;
  };
  getProductLinesInit({}, false, 1);

  let callbacks = {
    pullProductLines: pullProductLines,
    pullAvailableFilters: pullAvailableFilters,
  };
</script>

<style>
  .fizz-styles button {
    display: unset;
  }
</style>

<FormErrors {errors} />
<div class="grid-row padding-bottom--small margin-bottom--small">
  <div class="grid-container">
    {#each sellSheet.product_lines as productLine}
      <div
        class="grid-col c12-12 align-left background-white margin-bottom--smaller dpl-row"
        id="product-line-{productLine.id}"
      >
        <div class="grid-row padding--smaller">
          <div class="grid-col c12-12">
            <div class="media">
              <ProductImage
                product={productLine}
                width={80}
                height={80}
                fizz={false}
                auto={true}
                aspectRatio={"70"}
                usePlaceholderImage={true}
                className={"media-fixed distributors__inventory-image margin-right--small"}
              />
              <div class="media-fluid align-self-center">
                <div class="default-subheader--bold">{productLine.name}</div>
                {#if productLine.abv}
                  <div class="grid-col c12-12 margin-top--smallest">
                    <span class="s5 margin-top--smallest default-body--bold"> ABV: </span>
                    <span class="s5">
                      {productLine.abv}%
                    </span>
                  </div>
                {/if}
                {#if productLine.manufacturer}
                  <div class="grid-col c12-12 margin-top--smallest">
                    <span class="s5 margin-top--smallest default-body--bold"> Manufacturer: </span>
                    <span class="s5">
                      {productLine.manufacturer}
                    </span>
                  </div>
                {/if}
                {#if productLine.category}
                  <div class="grid-col c12-12 margin-top--smallest">
                    <span class="s5 margin-top--smallest default-body--bold"> Type: </span>
                    <span class="s5">
                      {productLine.category}
                    </span>
                  </div>
                {/if}
                {#if productLine.sell_sheet_products_attributes.length > 0}
                  <div class="s5 margin-top--smallest"
                    ><span class="default-body--bold">Sizes: </span>
                    {#each productLine.sell_sheet_products_attributes as product, i}
                      <div
                        >{ProductContainerDetails()(product)}
                        {#if product.unit_price || product.case_price}
                          <span>
                            for
                            {#if product.unit_price}
                              <span>${product.unit_price}/unit</span>
                            {/if}
                            {#if product.case_price}
                              {#if product.unit_price}
                                -
                              {/if}${product.case_price}/case
                            {/if}
                          </span>
                        {/if}

                        {#if product.unit_quantity || product.case_quantity}
                          <span
                            >{#if product.unit_price || product.case_price}.
                            {/if}Suggested Quantities:
                            {#if product.unit_quantity}
                              <span
                                >{product.unit_quantity}
                                {product.container_type}{#if product.unit_quantity != 1}s{/if}</span
                              >
                            {/if}
                            {#if product.case_quantity}
                              {#if product.unit_quantity}
                                -
                              {/if}{product.case_quantity} cases
                            {/if}
                          </span>
                        {/if}

                        {#if i < productLine.sell_sheet_products_attributes.length - 1},
                        {/if}
                      </div>
                    {/each}
                  </div>
                {/if}
                {#if productLine.product_type_name == "Wine"}
                  {#if productLine.vintage_years}
                    <div class="grid-col c12-12 margin-top--smallest">
                      <span class="s5 margin-top--smallest default-body--bold">
                        Vintage Years:
                      </span>
                      <span class="s5">
                        {arrayCleanup(productLine.vintage_years)}
                      </span>
                    </div>
                  {/if}

                  {#if productLine.wine_appellation}
                    <div class="grid-col c12-12 margin-top--smallest">
                      <span class="s5 margin-top--smallest default-body--bold"> Appellation: </span>
                      <span class="s5">
                        {productLine.wine_appellation}
                      </span>
                    </div>
                  {/if}

                  {#if productLine.wine_region}
                    <div class="grid-col c12-12 margin-top--smallest">
                      <span class="s5 margin-top--smallest default-body--bold"> Region: </span>
                      <span class="s5">
                        {productLine.wine_region}
                      </span>
                    </div>
                  {/if}

                  {#if productLine.wine_varietal}
                    <div class="grid-col c12-12 margin-top--smallest">
                      <span class="s5 margin-top--smallest"> Varietal: </span>
                      <span class="s5">
                        {productLine.wine_varietal}
                      </span>
                    </div>
                  {/if}
                {/if}
              </div>
              <div class="media-fixed align-right secondary-body--bold fizz-styles">
                <button
                  class="fizz-button-danger button--alert margin-right--smallest show--md"
                  on:click={() => remove(productLine)}
                  class:fizz-button-small={mobileMode}
                  disabled={page.loading}
                >
                  Remove
                </button>
                {#if !uneditableProducts}
                  <button
                    class="fizz-button button--primary show--md"
                    on:click={() => edit(productLine)}
                    class:fizz-button-small={mobileMode}
                  >
                    Edit Info
                  </button>
                {/if}
              </div>
            </div>
            <div class="grid-row">
              {#if productLine.description}
                <div
                  class="grid-col c12-12 margin-top--smallest padding-bottom--smaller sell-sheet-align-notes"
                >
                  <span class="s5 margin-top--smallest default-body--bold"> Description: </span>
                  <span class="s5" style="white-space: pre-line;">
                    {productLine.description}
                  </span>
                </div>
              {/if}
            </div>
            <div class="grid-row">
              {#if productLine.tasting_notes}
                <div
                  class="grid-col c12-12 margin-top--smallest padding-bottom--smaller sell-sheet-align-notes"
                >
                  <span class="s5 margin-top--smallest default-body--bold"> Tasting notes: </span>
                  <span class="s5" style="white-space: pre-line;">
                    {productLine.tasting_notes}
                  </span>
                </div>
              {/if}
            </div>
            <div class="grid-row">
              {#if productLine.additional_notes}
                <div
                  class="grid-col c12-12 margin-top--smallest padding-bottom--smaller sell-sheet-align-notes"
                >
                  <span class="s5 margin-top--smallest default-body--bold">
                    Additional Notes:
                  </span>
                  <span class="s5" style="white-space: pre-line;">
                    {productLine.additional_notes}
                  </span>
                </div>
              {/if}
            </div>
            <div class="grid-row hide--md">
              <div
                class="grid-col c12-12 margin-top--smallest padding-bottom--smaller sell-sheet-align-notes fizz-styles"
              >
                <button
                  class="fizz-button-danger button--alert margin-right--smallest fizz-button-small"
                  on:click={() => remove(productLine)}
                  disabled={page.loading}
                >
                  Remove
                </button>
                {#if !uneditableProducts}
                  <button
                    class="fizz-button button--primary fizz-button-small"
                    on:click={() => edit(productLine)}
                  >
                    Edit Info
                  </button>
                {/if}
              </div>
            </div>
          </div>
        </div>
        <div class="grid-row"><hr class="hr hr-card-separator" /></div>
      </div>
    {/each}
  </div>
</div>
<span class="fizz-styles">
  <ProductChooserModal
    {page}
    {callbacks}
    {showAddProductsModal}
    {closeAddProduct}
    {closeAddProductAndSave}
    {selectMultipleChoices}
    {initialFilterStateParams}
  />
</span>

<span class="fizz-styles">
  <EditSellSheetProductModal
    productLine={editingCopy}
    {sellSheet}
    bind:showEditModal
    {finishSaveEdits}
    {cancelEditsCB}
    {page}
  />
</span>
